import { logger } from "@bfc-software/app-toolbox";
import { Loader } from "@bfc-software/pallet";
import { useEffect, useRef, useState } from "react";

import styles from "./preset-dashboard.module.css";

type EmbeddedHandle = { unmount: () => void };

export type PresetDashboardProps = {
  /** The embedded-dashboard uuid, not the numeric id in the dashboard's own URL. */
  dashboardId: string;
  /** For example, https://bfc.us1a.app.preset.io */
  supersetDomain: string;
  /**
   * Returns a new Preset guest token. The SDK calls it again on its own schedule
   * before the token expires.
   *
   * Must be referentially stable. It is an effect dependency, so a new identity on
   * each render creates the iframe again continuously. The same applies to
   * `onFailure`.
   */
  fetchGuestToken: () => Promise<string>;
  iframeTitle: string;
  /** Rendered instead of the SDK. MSW and the tests use it. */
  stub?: React.ReactNode;
  /** Called only if the embed fails. */
  onFailure?: () => void;
};

/**
 * Wraps Preset's `embedDashboard`, which is imperative and asynchronous. It
 * changes a DOM node and it adds an iframe.
 *
 * Pass a `key` tied to the caller's scope. The guest token carries the row-level
 * security clause, so a change of scope must discard the component. It must not
 * give a token for the new scope to a frame that holds the old scope.
 */
export function PresetDashboard({
  dashboardId,
  supersetDomain,
  fetchGuestToken,
  iframeTitle,
  stub,
  onFailure,
}: PresetDashboardProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<"mounting" | "ready" | "failed">(
    "mounting",
  );

  // `stub` is a ReactNode, so it is a new element on each render and must not go
  // in the dependency list. Only its presence matters.
  const stubbed = Boolean(stub);

  useEffect(() => {
    if (stubbed) {
      return;
    }
    const node = mountRef.current;
    if (!node) {
      return;
    }

    let cancelled = false;
    let handle: EmbeddedHandle | undefined;

    void (async () => {
      try {
        // Dynamic, so the SDK stays out of the main bundle, and out of jsdom when
        // the page renders the stub.
        const { embedDashboard } = await import("@preset-sdk/embedded");
        const embedded = await embedDashboard({
          id: dashboardId,
          supersetDomain,
          mountPoint: node,
          fetchGuestToken,
          dashboardUiConfig: DASHBOARD_UI_CONFIG,
          // Preset reads the Referer header to check the embedding origin against
          // the dashboard's Allowed Domains. A stricter policy gets the embed
          // refused.
          referrerPolicy: "strict-origin-when-cross-origin",
          iframeTitle,
        });

        if (cancelled) {
          // embedDashboard is asynchronous, so the cleanup and a second effect
          // body can run before this promise settles. A fast navigation or the
          // double invoke of StrictMode causes this. Without this branch, the
          // late resolve leaves a second iframe and a second refresh timer
          // active.
          embedded.unmount();
          return;
        }
        handle = embedded;
        setPhase("ready");
      } catch (error) {
        logger.warn(`Preset embed failed: ${String(error)}`);
        if (!cancelled) {
          setPhase("failed");
          onFailure?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      // unmount() is what clears the refresh timer. In @preset-sdk/embedded@0.3.2
      // it is `replaceChildren(); clearTimeout(...)`. If this cleanup cleared the
      // DOM node instead, the timer would continue to request tokens forever.
      handle?.unmount();
    };
  }, [
    dashboardId,
    supersetDomain,
    fetchGuestToken,
    iframeTitle,
    stubbed,
    onFailure,
  ]);

  if (stubbed) {
    return <div className={styles.mount}>{stub}</div>;
  }

  return (
    <div className={styles.mount} data-test-role="preset-dashboard">
      {/*
        Rendered unconditionally. If the loading state hid this node,
        mountRef.current would be null when the effect runs and nothing would
        embed, so the loader covers it instead.
      */}
      <div ref={mountRef} className={styles.frame} />
      {phase === "mounting" && (
        <div className={styles.overlay}>
          <Loader type="page" />
        </div>
      )}
    </div>
  );
}

/**
 * Module level, because this is an effect dependency. An inline literal would be a
 * new object on each render and would embed the dashboard again each time.
 *
 * `hideTitle` leaves nothing on the page that names the dashboard, so `iframeTitle`
 * is its only accessible name. `hideChartControls: false` keeps export available.
 */
const DASHBOARD_UI_CONFIG = {
  hideTitle: true,
  hideChartControls: false,
  filters: { expanded: true, visible: true },
} as const;
